import React, { useState, useEffect } from "react";
import CategoryIcon from "@mui/icons-material/Category";
import CategoryMenuOverlay from "../CategoryMenuOverlay/CategoryMenuOverlay";
import styles from "./CategoryTree.module.css";

interface Category {
  id: number;
  name: string;
  url: string;
  children: Category[];
  categoryThumbnail?: string | null;
}

interface CategoryItemProps {
  category: Category;
  level: number;
  onCategoryClick: (category: Category) => void;
  isActive: boolean;
}

interface CategoryTreeProps {
  categories: Category[];
  loading: boolean;
  error: string | null;
}

const CategoryItem: React.FC<CategoryItemProps> = ({
  category,
  level,
  onCategoryClick,
  isActive,
}) => {
  const [svgContent, setSvgContent] = useState<string | null>(null);
  const [arrowSvgContent, setArrowSvgContent] = useState<string | null>(null);

  // Fetch SVG content dynamically
  useEffect(() => {
    const fetchSvg = async () => {
      if (category.categoryThumbnail) {
        try {
          const response = await fetch(`${category.categoryThumbnail}`);
          const svgText = await response.text();
          setSvgContent(svgText);
        } catch (error) {
          console.error("Error fetching SVG:", error);
        }
      }
    };

    fetchSvg();
  }, [category.categoryThumbnail]);

  // Fetch arrow down icon SVG content dynamically
  useEffect(() => {
    const fetchArrowSvg = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_IMAGE_FOLDER}/down_fc4f2359b0.svg`
        );
        const svgText = await response.text();
        setArrowSvgContent(svgText);
      } catch (error) {
        console.error("Error fetching arrow down SVG:", error);
      }
    };

    fetchArrowSvg();
  }, []);

  // Handle category click
  const handleClick = () => {
    onCategoryClick(category);
  };

  return (
    <li className={styles[`level${level}`]}>
      <div
        className={`${styles.categoryItem} ${isActive ? styles.active : ""}`}
        onClick={handleClick}
      >
        {/* Render the category thumbnail (SVG) */}
        {svgContent ? (
          <div
            className={`${styles.categoryThumbnail} ${
              isActive ? styles.activeThumbnail : ""
            }`}
            dangerouslySetInnerHTML={{
              __html: svgContent
                .replace(/stroke="[^"]*"/g, `stroke="var(--navbar-text-color)"`)
                .replace(
                  /fill="[^"]*"/g,
                  `fill="${isActive ? "var(--negro-grafito)" : "transparent"}"`
                ),
            }}
          />
        ) : (
          level === 1 && <CategoryIcon className={styles.icon} />
        )}

        <div className={styles.categoryName}>{category.name}</div>

        {/* Render the arrow down icon */}
        {level === 1 && arrowSvgContent && (
          <div
            className={`${styles.iconRight} ${isActive ? styles.rotated : ""}`}
            dangerouslySetInnerHTML={{
              __html: arrowSvgContent.replace(
                /fill="[^"]*"/g,
                `fill="var(--navbar-text-color)"`
              ),
            }}
          />
        )}
      </div>
    </li>
  );
};

const CategoryTree: React.FC<CategoryTreeProps> = ({
  categories,
  loading,
  error,
}) => {
  const [activeCategory, setActiveCategory] = useState<Category | null>(null);

  const handleCategoryClick = (category: Category) => {
    if (activeCategory && activeCategory.id === category.id) {
      setActiveCategory(null);
    } else {
      setActiveCategory(category);
    }
  };

  const closeOverlay = () => {
    setActiveCategory(null);
  };

  if (loading) return <p>Loading categories...</p>;
  if (error) return <p>Error: {error}</p>;

  return (
    <div className={styles.categoryTreeContainer}>
      <ul className={styles.root}>
        {categories.map((category) => (
          <CategoryItem
            key={category.id}
            category={category}
            level={1}
            onCategoryClick={handleCategoryClick}
            isActive={activeCategory?.id === category.id}
          />
        ))}
      </ul>

      {activeCategory && (
        <CategoryMenuOverlay
          topLevelCategory={activeCategory}
          onClose={closeOverlay}
        />
      )}
    </div>
  );
};

export default CategoryTree;
